import * as ko from 'knockout';
import { Request } from 'Core/Common/Request';
import { P } from 'Core/Common/Promise';
import { BlockUI } from 'Core/Common/BlockUi';

import Template from './OnlyOfficeDocumentEditorPage.html';

ko.templates['Pages/OnlyOfficeDocumentEditorPage/Templates/OnlyOfficeDocumentEditorPage'] = Template;

const PLACEHOLDER_ID = 'onlyoffice-editor-placeholder';
const API_SCRIPT_ID = 'onlyoffice-api-script';

export class OnlyOfficeDocumentEditorPage {
	private _isReady: KnockoutObservable<boolean>;
	private _editor: any;

	constructor() {
		this._isReady = ko.observable(false);
	}

	GetTemplateName(): string {
		return 'Pages/OnlyOfficeDocumentEditorPage/Templates/OnlyOfficeDocumentEditorPage';
	}

	Render(target: string): void {
		ko.cleanNode(document.getElementById(target));
		ko.applyBindings(this, document.getElementById(target));
	}

	AfterRender(): void {
		this._isReady(true);
		this.InitEditor();
	}

	private InitEditor(): void {
		BlockUI.Block();

		const deferred = P.defer<any>();
		Request
			.For('/api/OnlyOfficeDocumentEditorApi/GetEditorConfig?fileName=New Document.docx')
			.Get()
			.then((data: any) => deferred.resolve(data));

		deferred.promise().then((data: any) => {
			this.LoadApiScript(data.apiUrl)
				.then(() => this.CreateEditor(data.config))
				.catch(() => BlockUI.Unblock());
		});
	}

	// The ONLYOFFICE api.js is served same-origin through the reverse proxy so it is
	// not blocked as mixed content on the HTTPS host.
	private LoadApiScript(apiUrl: string): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			if ((window as any).DocsAPI) {
				resolve();
				return;
			}

			const existing = document.getElementById(API_SCRIPT_ID);
			if (existing) {
				existing.addEventListener('load', () => resolve());
				existing.addEventListener('error', () => reject());
				return;
			}

			const script = document.createElement('script');
			script.id = API_SCRIPT_ID;
			script.src = apiUrl;
			script.onload = () => resolve();
			script.onerror = () => reject();
			document.head.appendChild(script);
		});
	}

	private CreateEditor(config: any): void {
		const placeholder = document.getElementById(PLACEHOLDER_ID);

		// ONLYOFFICE replaces the placeholder div with its own iframe, so a height of
		// '100%' would resolve against a container that has no height and collapse to a
		// short strip. Give it an explicit pixel height instead.
		config.width = '100%';
		config.height = this.EditorHeight(placeholder) + 'px';
		config.events = {
			onDocumentReady: () => BlockUI.Unblock(),
			onError: () => BlockUI.Unblock()
		};

		this._editor = new (window as any).DocsAPI.DocEditor(PLACEHOLDER_ID, config);

		// After creation the same id resolves to the injected iframe; keep it sized to
		// the viewport on resize.
		window.addEventListener('resize', () => {
			const frame = document.getElementById(PLACEHOLDER_ID) as HTMLElement;
			if (frame)
				frame.style.height = this.EditorHeight(frame) + 'px';
		});
	}

	// Remaining viewport below the page header, with a floor so the editor is never
	// rendered in a short strip regardless of layout timing.
	private EditorHeight(el: HTMLElement): number {
		const top = el.getBoundingClientRect().top;
		return Math.max(600, Math.round(window.innerHeight - top - 4));
	}
}
